// src/components/pages/company/PurchasedVouchers.tsx

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import Pagination from '@/components/pages/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, SearchX, X } from 'lucide-react';
import Joyride from 'react-joyride';
import { SellBonusDialog } from '@/components/pages/dialogs/SellBonusDialog';
import { PieChart, Pie, Cell } from 'recharts';
import { getTourStyles } from '@/components/tour/tourStyles';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

import getPurchasedVouchersTourSteps from '@/components/tour/shared/PurchasedVouchersTourSteps';
import { useBonos } from '@/components/hooks/useBonos';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';

const COLORS = ['#103254', '#E5E7EB'];

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

export default function PurchasedVouchers() {
  const empresaId = localStorage.getItem('id_empresa') || '';
  const isMobile = window.innerWidth < 640;
  const {
    loading,
    pageData,
    refetch,
    filter,
    setFilter,
    eliminarBonoComprado,
    page,
    setPage,
    totalPages,
    pageSize,
    totalItems,
    changePageSize,
  } = useBonos(empresaId, { tipo: 'vendidos', pageSize: 10 });

  const [runTour, setRunTour] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const steps = getPurchasedVouchersTourSteps(isMobile);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando aún no hay tabla que
  // enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    await eliminarBonoComprado(deleteTarget.id_bono_comprado);
    setDeleteTarget(null);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8 pb-28 lg:pb-0"
    >
      {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Bonos vendidos
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped')
            setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* Filtro + Vender */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row gap-3 justify-between items-center"
      >
        <Input
          placeholder="Buscar por bono, servicio o cliente..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="tour-filter-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
        />

        <Button
          onClick={() => setOpenDialog(true)}
          className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 tour-sell-btn w-full sm:w-auto transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" /> Vender bono
        </Button>
      </motion.div>

      {/* Tabla + Estados */}
      <AnimatePresence>
        {loading ? (
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center h-32"
          >
            <motion.div
              variants={rowVariants}
              className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254]"
            />
          </motion.div>
        ) : pageData.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <SearchX className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Sin coincidencias
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No se encontraron bonos vendidos.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista móvil - Tarjetas */}
            <div className="block lg:hidden space-y-4">
              {pageData.map((v) => {
                const usados = v.usos_realizados || 0;
                const total = v.cantidad_servicios || 0;
                const data = [
                  { value: usados },
                  { value: Math.max(total - usados, 0) },
                ];

                return (
                  <motion.div
                    key={v.id_bono_comprado}
                    variants={rowVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <Card className={`rounded-xl ${GLASS_CARD}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-base truncate">
                              {v.nombre_bono}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {v.nombre_servicio}
                            </p>
                          </div>
                          {usados === 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteTarget(v)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 tour-delete-btn-mobile hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        <div className="space-y-1 pt-2 border-t border-gray-200 dark:border-white/10">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                              Cliente:
                            </span>
                            <span className="text-gray-800 dark:text-white">
                              {v.nombre_cliente || '-'}
                            </span>
                          </div>
                          {v.email_cliente && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500 dark:text-gray-400">
                                Email:
                              </span>
                              <span className="text-gray-800 dark:text-white truncate">
                                {v.email_cliente}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="tour-usos-chart">
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Usos
                            </span>
                            <div className="flex items-center gap-2 mt-1">
                              <PieChart width={32} height={32}>
                                <Pie
                                  data={data}
                                  innerRadius={10}
                                  outerRadius={16}
                                  dataKey="value"
                                  stroke="none"
                                >
                                  {data.map((_, index) => (
                                    <Cell key={index} fill={COLORS[index]} />
                                  ))}
                                </Pie>
                              </PieChart>
                              <span className="font-medium text-gray-800 dark:text-white">
                                {usados}/{total}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Vencimiento
                            </span>
                            <p className="font-medium text-gray-800 dark:text-white mt-1">
                              {v.fecha_vencimiento
                                ? new Date(
                                    v.fecha_vencimiento
                                  ).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })
                                : '-'}
                            </p>
                          </div>
                        </div>

                        {v.precio_total != null && (
                          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-white/10">
                            <span className="text-gray-500 dark:text-gray-400 text-sm">
                              Precio:
                            </span>
                            <span className="text-gray-900 dark:text-white font-semibold">
                              {v.precio_total.toFixed(2)} €
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Vista desktop - Tabla */}
            <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="overflow-x-auto p-0">
                <table className="min-w-full table-auto text-sm">
                  <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left">Bono</th>
                      <th className="px-4 py-3 text-left">Servicio</th>
                      <th className="px-4 py-3 text-left">Cliente</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Usos</th>
                      <th className="px-4 py-3 text-left">Vencimiento</th>
                      <th className="px-4 py-3 text-left">Precio</th>
                      <th className="px-4 py-3 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {pageData.map((v, idx) => {
                      const usados = v.usos_realizados || 0;
                      const total = v.cantidad_servicios || 0;

                      const data = [
                        { value: usados },
                        { value: Math.max(total - usados, 0) },
                      ];

                      return (
                        <motion.tr
                          key={v.id_bono_comprado}
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className={
                            idx % 2 === 0
                              ? 'bg-white dark:bg-transparent'
                              : 'bg-gray-50 dark:bg-white/[0.03]'
                          }
                        >
                          <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">
                            {v.nombre_bono}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            {v.nombre_servicio}
                          </td>
                          <td className="px-4 py-3 text-gray-800 dark:text-white">
                            {v.nombre_cliente || '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            {v.email_cliente || '-'}
                          </td>

                          <td className="px-4 py-3 text-gray-800 dark:text-white tour-usos-chart">
                            <div className="flex items-center gap-2">
                              <PieChart width={40} height={40}>
                                <Pie
                                  data={data}
                                  innerRadius={12}
                                  outerRadius={18}
                                  dataKey="value"
                                  stroke="none"
                                >
                                  {data.map((_, index) => (
                                    <Cell key={index} fill={COLORS[index]} />
                                  ))}
                                </Pie>
                              </PieChart>
                              <span className="text-sm font-medium">
                                {usados}/{total}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            {v.fecha_vencimiento
                              ? new Date(
                                  v.fecha_vencimiento
                                ).toLocaleDateString()
                              : '-'}
                          </td>

                          <td className="px-4 py-3 text-gray-800 dark:text-white">
                            {v.precio_total != null
                              ? `${v.precio_total.toFixed(2)} €`
                              : '-'}
                          </td>

                          <td className="px-4 py-3 text-center">
                            {usados === 0 ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteTarget(v)}
                                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 tour-delete-btn-desktop hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 text-xs italic">
                                No eliminable
                              </span>
                            )}
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Paginación */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={changePageSize}
              itemLabel="bonos vendidos"
              className="tour-pagination"
            />
          </>
        )}
      </AnimatePresence>

      {/* Confirmar eliminación (bottom sheet) */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-red-600 dark:text-red-400 pr-10">
                Eliminar bono
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setDeleteTarget(null)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                ¿Seguro que quieres eliminar el bono{' '}
                <strong className="text-gray-900 dark:text-white">
                  {deleteTarget?.nombre_bono}
                </strong>{' '}
                del cliente{' '}
                <strong className="text-gray-900 dark:text-white">
                  {deleteTarget?.nombre_cliente}
                </strong>
                ?
              </p>
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              Cancelar
            </Button>

            <Button
              onClick={handleConfirmDelete}
              className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SellBonusDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onSold={refetch}
      />
    </motion.div>
  );
}